import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { Button, Div, Loader, PageHeader, Text, useTheme, type ModalRef } from "react-better-html";

import { type Workspace } from "../../../types/workspace";
import { type PropertyFull } from "../../../types/property";

import { useParams, useReduxAction, useReduxState } from "../../../utils/hooks";
import { useI18n } from "../../../utils/i18n";

import WorkspaceModal from "../../../components/modals/WorkspaceModal";
import PropertiesWorkspacesList from "../../../components/PropertiesWorkspacesList";
import WorkspaceShareModal from "../../../components/modals/WorkspaceShareModal";

function SingleWorkspace() {
   const theme = useTheme();
   const { t } = useI18n();
   const { workspaceId } = useParams<{ workspaceId: Workspace["id"] }>();

   const workspaceModalRef = useRef<ModalRef>(null);
   const workspaceShareModalRef = useRef<ModalRef>(null);

   const workspacesList = useReduxState((state) => state.workspaces.list);
   const workspaceProperties = useReduxState((state) => state.properties.workspaceProperties);

   const getWorkspaceById = useReduxAction((actions) => actions.workspaces.getWorkspaceById);
   const getAllWorkspaceProperties = useReduxAction((actions) => actions.properties.getAllWorkspaceProperties);

   const onClickEdit = useCallback(() => {
      workspaceModalRef.current?.open();
   }, []);
   const onClickShare = useCallback(() => {
      workspaceShareModalRef.current?.open();
   }, []);
   const getSinglePropertyUrl = useCallback(
      (property: PropertyFull): string => `properties/${property.property.id}`,
      [],
   );

   const thisWorkspace = useMemo(
      () => workspacesList?.find((workspace) => workspace.id === workspaceId),
      [workspacesList, workspaceId],
   );

   useEffect(() => {
      if (!workspaceId) return;

      getWorkspaceById({
         id: workspaceId,
      });
      getAllWorkspaceProperties({
         id: workspaceId,
      });
   }, [workspaceId]);

   const thisWorkspaceProperties = workspaceId ? workspaceProperties?.[workspaceId] : undefined;

   return thisWorkspace ? (
      <>
         <PageHeader
            title={thisWorkspace.name}
            titleAs="h2"
            titleRightElement={
               thisWorkspaceProperties ? (
                  <Text
                     fontSize={14}
                     color={theme.colors.textSecondary}
                  >{`${thisWorkspaceProperties.items.length ?? 0} ${t((l) => (thisWorkspaceProperties.items.length === 1 ? l.common.property : l.common.properties))}`}</Text>
               ) : (
                  <Loader.text fontSize={14} size={10} />
               )
            }
            description={thisWorkspace.description}
            rightElement={
               <Div.row alignItems="center" gap={theme.styles.gap}>
                  <Button.secondary text={t((l) => l.common.share)} onClick={onClickShare} />
                  <Button icon="pen" text={t((l) => l.common.edit)} onClick={onClickEdit} />
               </Div.row>
            }
            marginBottom={theme.styles.space}
         />

         <PropertiesWorkspacesList properties={thisWorkspaceProperties} getSinglePropertyUrl={getSinglePropertyUrl} />

         <WorkspaceModal editedWorkspace={thisWorkspace} ref={workspaceModalRef} />

         <WorkspaceShareModal workspace={thisWorkspace} ref={workspaceShareModalRef} />
      </>
   ) : (
      <Loader.box />
   );
}

export default memo(SingleWorkspace);
